import { Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ImageUploader from '../../media/uploaders/ImageUploader';
import { Button, Flex, SubPage, Typo, Figure } from '../../../ui';
import { getUserThemeColor, USER_THEME_COLORS, getDarkerThemeColor, getLighterThemeColor } from '../../../ui/theme/colors';
import { CONTENT_SMALL } from '../../../utils/media';
import { useSettingsActions } from '../hooks/useSettingsActions';
import { useSettings } from '../hooks';
import Buttons from '../../../ui/Buttons';

const ColorBox = styled.div`
  min-width: 64px;
  min-height: 45px;
  background-color: ${({ color }) => color};
  margin: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
`;
const ColorBox2 = styled(ColorBox)`
  margin-left: 0;
`;

const Box = styled.div`
  display: flex;
  cursor: pointer;
  background-color: ${(props) => props.selected && props.theme.colors.NEU_PRIMARY};
  box-shadow: ${(props) => props.theme.shadows.BOX_SHADOW};
`;

const ColorInput = styled.input`
  margin: 8px;
  max-width: 32px;
  min-height: 32px;
  cursor: pointer;
  border: none;
  background-color: transparent;
  appearance: none;
  padding: 0;
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: none;
  }
`;

const HexInput = styled.input`
  margin: 8px;
  padding: 6px 8px;
  border: 1px solid ${(props) => props.theme.colors.NEU_200};
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  width: 100px;
`;

const Branding = () => {
  const { t } = useTranslation();
  const { updateSettings } = useSettingsActions();
  const [customColor, setCustomColor] = useState(false);
  const [colorSelected, setColorSelected] = useState();
  const [customSelected, setCustomSelected] = useState();
  const { settings } = useSettings();

  const colorTheme = settings.theme?.color;

  const selectThemeColor = useCallback((e) => {
    setCustomColor(false);
    const color = e.currentTarget.getAttribute('aria-label');
    setColorSelected(color);
  }, []);

  const resetColorThemeSelection = useCallback(() => {
    if (Object.keys(USER_THEME_COLORS).includes(colorTheme)) {
      setColorSelected(colorTheme);
    } else if (colorTheme) {
      setColorSelected(null);
      setCustomColor(true);
      setCustomSelected(colorTheme);
    }
  }, [colorTheme]);

  useEffect(() => {
    resetColorThemeSelection();
  }, [resetColorThemeSelection]);

  const saveBranding = useCallback(async () => {
    const neededColor = customColor ? customSelected : colorSelected;

    if (neededColor !== colorTheme || customColor) {
      const settingsData = {
        theme: { ...settings.theme, color: neededColor },
      };
      await updateSettings(settingsData);
    }
  }, [updateSettings, colorSelected, customSelected, settings.theme, colorTheme, customColor]);

  const saveBrandingLogo = useCallback(
    async (logo) => {
      if (logo) {
        const settingsData = {
          theme: { ...settings.theme, logoId: logo?.id, logoUrl: logo?.url },
        };
        await updateSettings(settingsData);
      }
    },
    [updateSettings, settings.theme],
  );

  return (
    <SubPage align='flex-start' size={CONTENT_SMALL} gap='40'>
      <Flex gap='30' direction='column' align='flex-start' minWidth='300px'>
        <Typo.TextTitle>{t('settings.logo')}</Typo.TextTitle>
        <ImageUploader saveByDelete={true} type={'single'} id={'companyLogo'} saveAction={saveBrandingLogo} imageToPreview={settings.theme?.logoUrl}>
          {settings.theme?.logoUrl && (
            <Figure.Rectangle $objectfit='contain'>
              <img alt={'imageToPreview'} src={settings.theme?.logoUrl} loading='lazy' />
            </Figure.Rectangle>
          )}
        </ImageUploader>
      </Flex>
      <Flex gap='30' direction='column' align='flex-start'>
        <Typo.TextTitle>{t('settings.themeColor')}</Typo.TextTitle>
        <Flex wrap={'wrap'} gap='20'>
          {Object.keys(USER_THEME_COLORS).map((color) => {
            const light = getLighterThemeColor(color);
            const dark = getDarkerThemeColor(color);
            return (
              <Tooltip key={color} title={color} placement='bottom' arrow>
                <div onClick={selectThemeColor}>
                  <Typo.SmallText>{color}</Typo.SmallText>
                  <Box selected={color === colorSelected}>
                    <ColorBox color={getUserThemeColor(color)}>
                      <Typo.TextTitle $themeColor={color}>{t('settings.text')}</Typo.TextTitle>
                    </ColorBox>
                    <div>
                      <ColorBox2 color={dark} />
                      <ColorBox2 color={light} />
                    </div>
                  </Box>
                </div>
              </Tooltip>
            );
          })}
        </Flex>

        <Flex gap='30' direction='column' align='flex-start'>
          <Typo.TextTitle>{t('settings.selectBrandColor')}</Typo.TextTitle>

          <Flex wrap={'wrap'} width={'100%'} align='center'>
            <ColorInput
              type='color'
              value={/^#[0-9a-fA-F]{6}$/i.test(customSelected) ? customSelected : '#808080'}
              onChange={(e) => setCustomSelected(e.target.value)}
            />
            <HexInput type='text' value={customSelected || ''} onChange={(e) => setCustomSelected(e.target.value)} placeholder='#808080' />
          </Flex>
          <Tooltip title={t('globals.custom')} placement='bottom' arrow>
            <Box
              selected={customSelected && !Object.keys(USER_THEME_COLORS).includes(colorSelected)}
              onClick={() => {
                setCustomColor(true);
                setColorSelected(null);
              }}
            >
              <ColorBox color={customSelected || '#808080'}>
                <Typo.TextTitle $themeColor={customSelected || '#808080'}>{t('settings.selectedColor')}</Typo.TextTitle>
              </ColorBox>
              {customSelected && (
                <div>
                  <ColorBox2 color={getDarkerThemeColor(customSelected)} />
                  <ColorBox2 color={getLighterThemeColor(customSelected)} />
                </div>
              )}
            </Box>
          </Tooltip>
        </Flex>

        <Flex justify='flex-end' gap='10' width='100%'>
          <Buttons.Cancel onClick={resetColorThemeSelection} />
          <Button onClick={saveBranding}>{t('globals.save')}</Button>
        </Flex>
      </Flex>
    </SubPage>
  );
};

export default Branding;
